import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import LeftNav, {
  MEDIA_QUERIES,
  NAV_EXPANDED_STATES,
  useStickyLeftNav,
} from '@decisiv/ui-components/lib/components/LeftNav';
import TopNav from '@decisiv/ui-components/lib/components/TopNav';

import AppsLinks from './AppsLinks';
import UserMenu from './UserMenu';
import Versions from './Versions';
import NAV_ITEMS from './navItems';
import {
  getPreferredNavExpandedState,
  setPreferredNavExpandedState,
} from './navStatePreference';
import {
  LeftNavContainer,
  NavColumnContainer,
  PageContainer,
  PrimaryContentContainer,
  ScrollingContent,
  TopNavBrandSection,
  TopNavContainer,
  TopNavSection,
} from './styles';

// MEDIA_QUERIES is ordered narrow → wide. This mirrors what LeftNav does
// internally to pick a state from the viewport; we compute it ourselves so we
// can hand LeftNav an explicit default — see the note in ManagementLayout.
function viewportNavExpandedState() {
  if (window.matchMedia(MEDIA_QUERIES[2]).matches) {
    return NAV_EXPANDED_STATES.EXPANDED;
  }
  if (window.matchMedia(MEDIA_QUERIES[1]).matches) {
    return NAV_EXPANDED_STATES.COLLAPSED_X;
  }
  return NAV_EXPANDED_STATES.COLLAPSED_XY;
}

/**
 * KeyDS application shell for the Management tool: collapsible LeftNav beside a
 * TopNav and a scrolling content area.
 *
 * Modeled on managed-care-ui's `components/Layout`, with two differences forced
 * by portal being a Rails app rather than a SPA:
 *
 *   - There is no react-router, so no `<Outlet />`. Each page mounts its own
 *     component and passes it as `children`.
 *   - Nav items are plain URLs and navigation is a full page load, so the
 *     active item comes from `currentPath` (supplied by the controller) rather
 *     than from router state.
 */
function ManagementLayout({
  appLinks,
  brandName,
  children,
  currentPath,
  currentUser,
  footer,
}) {
  const [
    { isExpanded, navExpandedState },
    handleChangeNavExpandedState,
  ] = useStickyLeftNav({
    getPreferredNavExpandedState,
    setPreferredNavExpandedState,
  });

  // LeftNav must always receive a defined state. Left fully uncontrolled (no
  // navExpandedState and no defaultNavExpandedState) it mounts as COLLAPSED_XY
  // and then switches to the viewport state in an effect, which throws
  // "Rendered fewer hooks than expected" on the React 16 that portal pins.
  // Passing an explicit default keeps the responsive initial state without
  // that transition. Reproduced in ui-components 2.0.1-alpha.226.
  const defaultNavExpandedState = useMemo(viewportNavExpandedState, []);

  return (
    <PageContainer>
      <NavColumnContainer>
        <LeftNavContainer $isExpanded={isExpanded}>
          <LeftNav
            currentPath={currentPath}
            defaultNavExpandedState={defaultNavExpandedState}
            items={NAV_ITEMS}
            navExpandedState={navExpandedState}
            onChangeNavExpandedState={handleChangeNavExpandedState}
          />
        </LeftNavContainer>
        {/* Only while expanded: there is no room for it beside the icon rail. */}
        {isExpanded && footer && <Versions {...footer} />}
      </NavColumnContainer>
      <PrimaryContentContainer>
        <TopNavContainer>
          <TopNav>
            <TopNavBrandSection>
              <TopNav.BrandInfo primaryText={brandName} />
            </TopNavBrandSection>
            <TopNavSection>
              {currentUser && <UserMenu {...currentUser} />}
              <AppsLinks links={appLinks} />
            </TopNavSection>
          </TopNav>
        </TopNavContainer>
        <ScrollingContent>{children}</ScrollingContent>
      </PrimaryContentContainer>
    </PageContainer>
  );
}

ManagementLayout.propTypes = {
  appLinks: AppsLinks.propTypes.links,
  brandName: PropTypes.string,
  children: PropTypes.node.isRequired,
  currentPath: PropTypes.string,
  currentUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    logoutPath: PropTypes.string.isRequired,
    name: PropTypes.string,
    profilePath: PropTypes.string,
    roleLabel: PropTypes.string,
  }),
  footer: PropTypes.shape(Versions.propTypes),
};

ManagementLayout.defaultProps = {
  appLinks: [],
  brandName: 'User Management',
  currentPath: undefined,
  currentUser: undefined,
  footer: undefined,
};

export default ManagementLayout;
